import { useState } from 'react';
import IconArrow from "../../assets/icons/icon_arrow";
import Switch from "../_common/ui/switch/switch";
import Tooltip from '../_common/ui/tooltip/tooltip';


function Analytics({analytics, status}: {analytics: any, status: string}) {
	const [isDetail, setIsDetail] = useState<boolean>(false)

	const format = (currency: string, amount: any) => {
		try {
			return Intl.NumberFormat(navigator.language, {
				style: "currency",
				currency: currency,
			}).format(parseFloat(amount));
		} catch (e) {
			console.error(e)
			return amount
		}
		
	}

	const isEnoughData = (data: any) => {
		const getLevel = (controlVal: any, variantVal: any): number => {
			const c = parseFloat(controlVal || 0);
			const v = parseFloat(variantVal || 0);
			if (c < 30 || v < 30) return 0; // Warming-up
			if (c < 100 || v < 100) return 1; // Trends
			return 2; // Reliable
		};

		return {
			orders: getLevel(data?.control?.total_orders, data?.variant?.total_orders),
			atc: getLevel(data?.control?.total_atc, data?.variant?.total_atc)
		};
	};

	const calculateStatus = (prob: number, up: number): any => {
		let metric: any = {}

		// NEUTRAL
		if (up === 0) {
			metric = {message: `No difference observed. \nThe performance is identical to the control.`, status: 'neutral'};
		}

		// ✅ WINNER !!!!!!!!!!!
		if (prob >= 95 && up >= 1) {
			metric = {message: `Strong positive trend.\nThe odds are in favor of this variant (+${up}%).`, status: 'winner'};
		}

		// ⚠️ WINNER BUT UPLIFT TOO LOW
		if (prob >= 95 && up > 0 && up < 1) {
			metric = {message: `Statistically better, but low impact.\nThe gain is too small (+${up}%) to be meaningful.`, status: 'neutral-up'}
		}

		// 🟢 WINNER : ON THE WAY
		if (prob >= 80 && prob < 95 && up >= 1) {
			metric = {message: `Promising trend. The odds are \nin favor of this variant (+${up}%).`, status: 'winner'}
		}

		// ⚠️ WINNER : ON THE WAY, uplift negligible
		if (prob >= 80 && prob < 95 && up > 0 && up < 1) {
			metric = {message:` Trending positive, but impact looks low.\n The potential gain (+${up}%) seems negligible.`, status: 'neutral-up'}
		}

		// ❌ LOSER !!!!!!!!!!!
		if (prob <= 5 && up <= -1) {
			metric = {message:`Strong negative trend.\nThe variant appears to be performing lower.`, status: 'loser'}
		}

		// ⚠️ LOSER BUT UPLIFT TOO LOW
		if (prob <= 5 && up < 0 && up > -1) {
			metric = {message: `Statistically worse, but low impact.\n The drop is minimal (${up}%), but the control is technically better.`, status: 'neutral-down'}
		}

		// 🔴 LOSER : ON THE WAY
		if (prob <= 20 && prob > 5 && up <= -1) {
			metric = {message: `Negative trend.\nThe variant appears to be performing lower.`, status: 'loser'}
		}

		// ⚠️ LOOSER : ON THE WAY, uplift negligible
		if (prob <= 20 && prob > 5 && up < 0 && up > -1) {
			metric = {message: `Trending negative, but impact looks low.\n Slight drop observed (${up}%), but nothing alarming yet.`, status: 'neutral-down'}
		}

		// ⚠️ NO DECISION
		if (up !== 0 && prob > 20 && prob < 80 ) {
			metric = {message: `Inconclusive. No clear winner yet.\nKeep the test running to gather more data.`, status: 'neutral'}
		}
		return metric
	}

	const displayMetric = (label: string, prob: number, up: number, metric: any, dataLevel: number, confidence: any, type: string): any => {
		return (
			<div className={`analytics__metric ${dataLevel === 0 ? 'collecting' : `${metric.status} ${up < 0 ? 'down' : ''}`}`}>
				{/* Metric label */}
				<div className="analytics__metric-label">
					{label}
				</div>

				{/* Metric result */}
				<div className="analytics__metric-result">
					{up !== 0 ? 
						<IconArrow /> 
					:''}
					<div className="analytics__metric-bloc">

						{dataLevel !== 0 ? 
							<>
								<p className="analytics__metric-uplift">
									Uplift: {up > 0 ? '+' : ''}{up}%
								</p>

								<p className="analytics__metric-conf">
									Confidence interval : <br/> [{confidence.lower}%, {confidence.upper}%] 
								</p>	

								<p className="analytics__metric-prob">
									Probability to win:  <br/>{prob}%
								</p>

							</>
						: 
							<p className={`analytics__metric-collecting ${status === 'live' ? 'anim' : ''}`}>
								{
									status === 'live' ? 'Gathering data' : 
									status === 'paused'  ? 'Paused': 
									status === 'completed'  ? 'Completed' : 
								''}
							</p>
						}
					</div>
				</div>
								
				{/* Metric conclusion */}
				{dataLevel === 2 ?
					<div className="analytics__metric-conclusion">
						{metric.message}
					</div>
				: dataLevel === 1 ?
					<div className="analytics__metric-conclusion collecting">
						Trends visible. <br/> 
						Reach 100 {type === 'atc' ? 'add to carts' : 'conversions'} to confirm results.
					</div>
				: 
					<div className="analytics__metric-conclusion collecting">
						{status !== 'completed' ? 
							<>
								Meaningful trends will appear after <br/>
								30 {type === 'atc' ? 'add to carts' : 'conversions'} per variant.

							</>
						: status === 'completed' ? 
							`All done! Lyntest needs at least 30 ${type === 'atc' ? 'add to carts' : 'conversions'} per variant to show a trend.` : ''
						}
					</div>
				}

			</div>
		)
	};

	return (
		<div className="analytics">
			{ analytics?.length ? 
				analytics.map((a: any, k: number) => {
					const resData = isEnoughData(a)

					const upCvr = parseFloat(a?.resBayesianCvr?.uplift || 0)
					const upRpv = parseFloat(a?.resBayesianRpv?.uplift || 0)
					const upAtc = parseFloat(a?.resBayesianAtc?.uplift || 0)

					const probCvr = parseFloat(a?.resBayesianCvr?.probability || 0)
					const probRpv = parseFloat(a?.resBayesianRpv?.probability || 0)
					const probAtc = parseFloat(a?.resBayesianAtc?.probability || 0)

					const statusCvr = calculateStatus(probCvr, upCvr)
					const statusRpv = calculateStatus(probRpv, upRpv)
					const statusAtc = calculateStatus(probAtc, upAtc)


					const confidenceCvr = {
						marginOfError: parseFloat(a?.resBayesianCvr?.marginOfError || 0),
						lower: parseFloat(a?.resBayesianCvr?.credibleInterval?.lower || 0),
						upper: parseFloat(a?.resBayesianCvr?.credibleInterval?.upper || 0)
					}

					const confidenceRpv = {
						marginOfError: parseFloat(a?.resBayesianRpv?.marginOfError || 0),
						lower: parseFloat(a?.resBayesianRpv?.credibleInterval?.lower || 0),
						upper: parseFloat(a?.resBayesianRpv?.credibleInterval?.upper || 0)
					}

					const confidenceAtc = {
						marginOfError: parseFloat(a?.resBayesianAtc?.marginOfError || 0),
						lower: parseFloat(a?.resBayesianAtc?.credibleInterval?.lower || 0),
						upper: parseFloat(a?.resBayesianAtc?.credibleInterval?.upper || 0)
					}
					
					return (
						<div className="analytics__analytic" key={k}>
							<div className="analytics__title">
								{a.view !== 'global' ? 'Product page performance : ': 'Total store performance'}
								{a.view !== 'global' ? a.view : ''}
							</div>


							<div className="analytics__label top">
								Variant comparison
							</div>

							<div className="analytics__table-detail" onClick={() => setIsDetail(!isDetail)}>
								<span className="analytics__table-detail-label">
									Show details:
								</span>
								<Switch value={isDetail} color='green'/>
							</div>

							<div className={`analytics__table ${isDetail ? 'detail' : ''}`}>

								{/* Title row */}
								<div className="analytics__table-row title">
									<div className="analytics__table-cell">
									</div>
									<div className="analytics__table-cell">
										Visitors
									</div>
									{isDetail ? 
										<>
											<div className="analytics__table-cell">
												Add to cart
											</div>

											<div className="analytics__table-cell">
												Sales <Tooltip content="Net Sales : the amount paid by customers after discounts, excluding taxes and shipping." />
											</div>
											<div className="analytics__table-cell">
												Orders
											</div>
											<div className="analytics__table-cell">
												Average order value 
											</div>
										</>
									: ''}

									<div className="analytics__table-cell">
										Add to cart rate <Tooltip content="Unique visitors who added to cart / Total visitors." />
									</div>
									<div className="analytics__table-cell">
										Conversion rate   <Tooltip content="Unique visitors who purchased / Total visitors." />
									</div>
									<div className="analytics__table-cell">
										Revenue per visitor <Tooltip content="Total revenue generated / Total visitors" option={{right: isDetail}}/>
									</div>
								</div>

								{/* data rows */}
								{['control', 'variant'].map((v, i) => {
										return (
											<div className="analytics__table-row" key={i}>
												<div className="analytics__table-cell">
													{v}
												</div>
												{/* total visitors */}
												<div className="analytics__table-cell">
													{a[v].total_visitors}
												</div>

												{isDetail ? 
													<>
														{/* total add to cart */}
														<div className="analytics__table-cell">
															{a[v].total_atc}
														</div>

														{/* total sales */}
														<div className="analytics__table-cell">
															{a.currency? format(a.currency, a[v].total_sales) : a[v].total_sales}
														</div>

														{/* total orders */}
														<div className="analytics__table-cell">
															{a[v].total_orders}
														</div>

														{/* AOV */}
														<div className="analytics__table-cell">
															{a.currency ? format(a.currency, a[v].aov) : a[v].aov}
														</div>

													</>
												: ''}
												
												{/* ATC rate */}
												<div className="analytics__table-cell">
													{a[v].atc_rate} %
													{v === 'variant' && resData.atc ? 
														<span className={`analytics__table-cell-uplift ${statusAtc.status}`}> 
															{upAtc > 0 ? '+ ' : ''}{upAtc}%
														</span>
													: ''}
												</div>

												{/* CVR */}
												<div className="analytics__table-cell">
													{a[v].conversion_rate} %
													{v === 'variant' && resData.orders ? 
														<span className={`analytics__table-cell-uplift ${statusCvr.status}`}> 
															{upCvr > 0 ? '+ ' : ''}{upCvr}%
														</span>
													: ''}
												</div>

												{/* RPV */}
												<div className="analytics__table-cell">
													{a.currency ? format(a.currency, a[v].rpv) : a[v].rpv}

													{v === 'variant' && resData.orders ? 
														<span className={`analytics__table-cell-uplift ${statusRpv.status}`}> 
															{upRpv > 0 ? '+ ' : ''}{upRpv}%
														</span>
													: ''}
												</div>

											</div>
										)
									})
								}
							</div>


							<div className="analytics__label">
								Projected gains
								<a className="analytics__metric-link" href="https://doc.lyntest.com/#how-to-read-your-results-" target="_blank" rel="noreferrer">
									How to read your results ?
								</a>
							</div>
						
							<div className="analytics__analysis">
								<div className="analytics__metrics">
									{/* ATC rate */}
									{displayMetric('Add to cart rate', probAtc, upAtc, statusAtc, resData.atc,confidenceAtc, 'atc')}

									{/* Conversion Rate */}
									{displayMetric('Conversion rate', probCvr, upCvr, statusCvr, resData.orders, confidenceCvr,'order')}
									
									{/* Revenue Per Visitor */}
									{displayMetric('Revenue per visitor', probRpv, upRpv, statusRpv, resData.orders, confidenceRpv, 'order')}
								</div>
							</div>
						</div>	
					)
				})
			:
				<div className="analytics__empty" >
					Waiting for your first visitors. Analytics will show up here.
				</div>
			}
		
		</div>
	);
}

export default Analytics;
